import { useState } from 'react';
import {
  useCreateOauthIntegration,
  useDeleteIntegration,
  useGetAvailableIntegrations,
  useGetIntegrations
} from 'apis/business/setting/integrations/hook';
import Spinner from 'components/Common/Spinner';
import Grid from '@mui/material/Grid2';
import { Box, Typography } from '@mui/material';
import Button from 'components/Common/Button';
import Search from 'components/Common/Search';
import IntegrationForm from './Components/IntegrationForm';
import { FaPlus } from 'react-icons/fa6';
import { Outlet, useNavigate } from 'react-router-dom';
import { CiWifiOff } from 'react-icons/ci';
import { formatDate } from 'utils/helperFunctions';
import { IoWifiSharp } from 'react-icons/io5';

export default function Integrations() {
  const [isModalOpen, setIsModalOpen] = useState<any>(false);
  const [searchText, setSearchText] = useState<null | string>(null);
  const navigate = useNavigate();

  const { data: availableIntegrations, isLoading: isAvailableIntegrationLoading } = useGetAvailableIntegrations();
  const { data: integrations, isLoading: isIntegrationLoading } = useGetIntegrations({
    'filter[name_cont]': searchText
  });
  const { mutateAsync: handleCreateIntegration } = useCreateOauthIntegration();
  const { mutateAsync: deleteIntegration } = useDeleteIntegration();

  const combinedData =
    integrations?.data?.map((item) => {
      const matchingIntegration = availableIntegrations?.data?.find(
        (integration: any) => integration.code === item.code
      );
      return {
        ...item,
        description: matchingIntegration?.description,
        sub_name: matchingIntegration?.name,
        avatar_url: matchingIntegration?.small_avatar_url,
        connection_type: matchingIntegration?.connection_type
      };
    }) ?? [];

  const handleActiveIntegration = async (code: string) => {
    const integration = availableIntegrations?.data?.find((item: any) => item.code === code);
    if (integration?.connection_type === 'oauth') {
      handleCreateIntegration(code as any);
    } else {
      setIsModalOpen(code);
    }
  };

  if (isIntegrationLoading || isAvailableIntegrationLoading) {
    return <Spinner />;
  }

  return (
    <>
      <Box display='flex' justifyContent='space-between' marginBottom={2}>
        <Box>
          <Typography variant='h2' fontWeight={500} mb={1}>
            Integrations
          </Typography>
          <Typography color='secondary' fontSize={14}>
            Manage your integrations and connect external services
          </Typography>
        </Box>
        <Button sx={{ height: 32, display: 'flex', gap: 1 }} onClick={() => navigate('add')}>
          <FaPlus />
          <Typography>New Integration</Typography>
        </Button>
      </Box>
      <Search
        size='small'
        placeholder='Search Integration'
        searchText={searchText}
        setSearchText={setSearchText}
        fullWidth
      />
      <Grid container spacing={2} mt={3}>
        {combinedData?.map((item, index) => {
          return (
            <Grid
              key={index}
              size={{ xs: 12, md: 6, lg: 4 }}
              bgcolor='white'
              p={2}
              border='1px solid #ECECEC'
              borderRadius={2}
              display='flex'
              flexDirection='column'
            >
              <Box display='flex' justifyContent='space-between' alignItems='center'>
                <Box display='flex' alignItems='center' gap={1.5} marginBottom={2}>
                  <img
                    src={item.avatar_url}
                    alt={item.name}
                    style={{
                      width: 32,
                      height: 32
                    }}
                  />
                  <Box display='flex' flexDirection='column'>
                    <Typography fontSize={16} fontWeight={500} lineHeight={1} marginBottom={0.2}>
                      {item.name}
                    </Typography>
                    <Typography fontSize={14} color='text.secondary' lineHeight={1}>
                      {item.sub_name}
                    </Typography>
                  </Box>
                </Box>
                <Box fontSize={24} marginBottom={3} color={item?.active ? '#228B22' : '#FF6347'}>
                  {item?.active ? <IoWifiSharp /> : <CiWifiOff />}
                </Box>
              </Box>
              <Typography my={1} fontSize={14} lineHeight={1.5}>
                {`${item?.description ? item?.description.slice(0, 80) : 'No Description'}...`}
              </Typography>
              <Typography mb={1} color='secondary' fontSize={11}>
                Modified at : {formatDate(item.updated_at)}
              </Typography>
              <Box flexGrow={1} />
              <Box display='flex' justifyContent='flex-end' gap={1.5}>
                <Button size='small' onClick={() => navigate(`${item.id}`)} color='info'>
                  Configure
                </Button>
                {item?.active ? (
                  <Button size='small' onClick={() => deleteIntegration(item.id!)} color='error'>
                    Deactivate
                  </Button>
                ) : (
                  <>
                    <Button size='small' onClick={() => handleActiveIntegration(item.code)}>
                      Activate
                    </Button>
                    <Button size='small' color='error' onClick={() => deleteIntegration(item.id!)}>
                      Delete
                    </Button>
                  </>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>
      <IntegrationForm open={isModalOpen} handleClose={() => setIsModalOpen(false)} />
      <Outlet />
    </>
  );
}
